// src/components/molecules/charts/base-agent-calls-chart/base-agent-calls-chart.tsx

import React, { useMemo } from 'react';
import { Bar } from 'react-chartjs-2';
import type { ChartData } from 'chart.js';
import { AGENT_COLORS } from 'src/utils/charts';
import type { BaseAgentCallsChartProps } from 'src/types/call-summaries';
import { Text } from 'src/modules/shared/components/atoms/text';
import { totalDatalabelPlugin } from '../utils';
import '../chartjs-registry';

const getChartOptions = () => ({
  responsive: true,
  maintainAspectRatio: false,
  plugins: {
    legend: {
      display: false,
    },
    datalabels: totalDatalabelPlugin,
  },
  scales: {
    y: { stacked: true },
    x: { stacked: true },
  },
});

const BaseAgentCallsChart: React.FC<BaseAgentCallsChartProps> = ({
  labels,
  agentPeriodsCallSummaries,
  chartTitle,
}) => {
  const chartData = useMemo(
    (): ChartData<'bar'> => ({
      labels,
      datasets: agentPeriodsCallSummaries.map((agentSummary) => {
        const summedCalls = agentSummary.effectiveCalls.map(
          (call, index) =>
            call +
            agentSummary.missedCalls[index] +
            agentSummary.voicemailCalls[index]
        );

        return {
          label: agentSummary.agentSummary.name,
          data: summedCalls,
          backgroundColor:
            AGENT_COLORS[agentSummary.agentSummary.id % AGENT_COLORS.length],
        };
      }),
    }),
    [agentPeriodsCallSummaries, labels]
  );

  const options = useMemo(() => getChartOptions(), []);

  return (
    <section className="min-w-0 overflow-hidden rounded-2xl border border-neutral-200 bg-white shadow-sm">
      <div className="border-b border-neutral-100 px-5 py-4">
        <Text as="h3" size="h6" weight="semibold" className="text-neutral-900">
          {chartTitle}
        </Text>
      </div>
      <div className="h-[320px] px-4 py-4">
        <Bar options={options} data={chartData} />
      </div>
    </section>
  );
};

export default BaseAgentCallsChart;
